import React from 'react';
import type { GeneratedNpc } from '@bid/shared-types';

interface NPCDisplayProps {
  npc: GeneratedNpc;
  onSave?: () => void;
}

export const NPCDisplay: React.FC<NPCDisplayProps> = ({ npc, onSave }): JSX.Element => {
  const cardStyle: React.CSSProperties = {
    backgroundColor: '#16162a',
    borderRadius: '12px',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: '1px solid rgba(212, 175, 55, 0.2)',
    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, transparent 100%)',
  };

  const nameStyle: React.CSSProperties = {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#d4af37',
    margin: 0,
    fontFamily: 'Cinzel, serif',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '12px',
    fontWeight: 600,
    color: '#d4af37',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    marginBottom: '12px',
  };

  const infoGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  };

  const infoItemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  };

  const infoLabelStyle: React.CSSProperties = {
    fontSize: '12px',
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const infoValueStyle: React.CSSProperties = {
    fontSize: '16px',
    color: '#e5e7eb',
    fontWeight: 500,
  };

  const ratingsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '8px',
  };

  const ratingItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    backgroundColor: '#0f0f1e',
    borderRadius: '6px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const ratingNameStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#9ca3af',
    textTransform: 'capitalize' as const,
  };

  const ratingValueStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 700,
    color: '#d4af37',
  };

  const abilitiesListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  };

  const abilityItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: '#0f0f1e',
    borderRadius: '6px',
    border: '1px solid rgba(212, 175, 55, 0.1)',
  };

  const abilityBulletStyle: React.CSSProperties = {
    width: '6px',
    height: '6px',
    backgroundColor: '#d4af37',
    borderRadius: '50%',
    flexShrink: 0,
  };

  const abilityTextStyle: React.CSSProperties = {
    fontSize: '14px',
    color: '#e5e7eb',
  };

  const saveButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: '#d4af37',
    color: '#0f0f1e',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginTop: '8px',
    boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)',
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <h2 style={nameStyle}>{npc.name}</h2>
      </div>

      <div style={contentStyle}>
        {/* Basic Info */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Character Info</h3>
          <div style={infoGridStyle}>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Playbook</span>
              <span style={infoValueStyle}>{npc.playbook}</span>
            </div>
            <div style={infoItemStyle}>
              <span style={infoLabelStyle}>Vice</span>
              <span style={infoValueStyle}>{npc.vice}</span>
            </div>
            {npc.vicePurveyor && (
              <div style={infoItemStyle}>
                <span style={infoLabelStyle}>Purveyor</span>
                <span style={infoValueStyle}>{npc.vicePurveyor}</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Ratings */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Action Ratings</h3>
          <div style={ratingsGridStyle}>
            {Object.entries(npc.actionRatings).map(
              ([action, rating]) =>
                rating > 0 && (
                  <div key={action} style={ratingItemStyle}>
                    <span style={ratingNameStyle}>{action}</span>
                    <span style={ratingValueStyle}>{rating}</span>
                  </div>
                )
            )}
          </div>
        </div>

        {/* Special Abilities */}
        <div style={sectionStyle}>
          <h3 style={sectionTitleStyle}>Special Abilities</h3>
          <div style={abilitiesListStyle}>
            {npc.specialAbilities.map((ability, index) => (
              <div key={index} style={abilityItemStyle}>
                <div style={abilityBulletStyle} />
                <span style={abilityTextStyle}>{ability}</span>
              </div>
            ))}
          </div>
        </div>

        {onSave && (
          <button
            onClick={onSave}
            style={saveButtonStyle}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#e5c158';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 55, 0.4)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#d4af37';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(212, 175, 55, 0.3)';
            }}
          >
            Save NPC
          </button>
        )}
      </div>
    </div>
  );
};
